/**
 * YearEndBoxesDialog — ajustement manuel des cases T4 / RL-1 d'un employé (valeur calculée en repère, ajustement conservé et surligné). Batch #14.264.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RotateCcw, Save, X } from "lucide-react";
import { API, authH, fmtCAD, Btn, err } from "./_shared";

const T4 = [["box14", "14 Revenus d'emploi"], ["box17", "17 Cotisations RRQ"], ["box17a", "17A RRQ 2e cotisation"], ["box18", "18 Cotisations AE"], ["box20", "20 Cotisations RPA"],
  ["box22", "22 Impôt fédéral retenu"], ["box24", "24 Gains assurables AE"], ["box26", "26 Gains ouvrant droit RRQ"], ["box44", "44 Cotisations syndicales"], ["box55", "55 Cotisations RPAP"], ["box56", "56 Gains assurables RPAP"]];
const T4_OTHER = [["30", "30 Pension et logement"], ["32", "32 Voyages (zone visée)"], ["34", "34 Usage personnel véhicule"], ["36", "36 Prêt à intérêt faible"], ["38", "38 Options d'achat de titres"],
  ["39", "39 Déduction options (110(1)d)"], ["40", "40 Autres avantages imposables"], ["42", "42 Commissions d'emploi"], ["66", "66 Allocation de retraite admissible"], ["67", "67 Allocation de retraite non admissible"], ["71", "71 Employé indien (statut)"], ["77", "77 Indemnités d'accident du travail"]];
const RL1 = [["A", "A Revenus d'emploi"], ["B_A", "B.A Cotisation RRQ"], ["B_B", "B.B 2e cotisation RRQ"], ["C", "C Cotisation AE"], ["D", "D Cotisation RPA"], ["E", "E Impôt du Québec"], ["F", "F Cotisation syndicale"],
  ["G", "G Salaire admissible RRQ"], ["H", "H Cotisation RQAP"], ["I", "I Salaire admissible RQAP"], ["J", "J Régime privé d'assurance maladie"], ["K", "K Voyages (région éloignée)"], ["L", "L Autres avantages"], ["M", "M Commissions"],
  ["N", "N Dons de bienfaisance"], ["O", "O Autres revenus"], ["P", "P Régime d'assurance interentreprises"], ["Q", "Q Salaires différés"], ["R", "R Revenu « situé » dans une réserve"], ["S", "S Pourboires reçus"], ["T", "T Pourboires attribués"],
  ["U", "U Retraite progressive"], ["V", "V Nourriture et logement"], ["W", "W Véhicule à moteur"]];

function Grid({ title, items, values, computed, onChange, prefix }) {
  return (
    <div>
      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">{title}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map(([k, label]) => {
          const calc = Number(computed?.[k] ?? 0); const cur = values[k];
          const changed = cur !== undefined && cur !== "" && Number(cur) !== calc;
          return (
            <label key={k} className={`block rounded-lg border p-2 ${changed ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
              <span className="block text-[10px] text-slate-500 leading-tight">{label}</span>
              <input type="number" step="0.01" min="0" value={cur ?? ""} placeholder={calc.toFixed(2)} onChange={(e) => onChange(k, e.target.value)}
                className="w-full bg-transparent text-sm font-mono outline-none" data-testid={`${prefix}-${k}`} />
              {changed && <span className="block text-[10px] text-amber-700">calculé : {fmtCAD(calc)}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function YearEndBoxesDialog({ masterId, year, row, onClose, onSaved }) {
  const ov = row.overrides || {};
  const [t4, setT4] = useState(() => Object.fromEntries(T4.map(([k]) => [k, ov.t4?.[k] ?? ""])));
  const [other, setOther] = useState(() => Object.fromEntries(T4_OTHER.map(([k]) => [k, ov.t4?.other?.[k] ?? ""])));
  const [rl1, setRl1] = useState(() => Object.fromEntries(RL1.map(([k]) => [k, ov.rl1?.[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v !== undefined && v !== null));

  const save = async (reset = false) => {
    setBusy(true);
    try {
      const body = reset ? {} : { t4: { ...clean(t4), ...(Object.keys(clean(other)).length ? { other: clean(other) } : {}) }, rl1: clean(rl1) };
      await axios.put(`${API}/admin/payroll/${masterId}/yearend/${year}/slips/${row.employee_id}/overrides`, body, { headers: authH() });
      toast.success(reset ? "Cases réinitialisées aux valeurs calculées" : "Ajustements enregistrés");
      onSaved();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="paie-yearend-boxes-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="font-bold text-slate-900">Modifier les cases — {row.display_name} · {year}</h3>
            <p className="text-xs text-slate-500 mt-1">Laissez vide pour garder la valeur calculée (affichée en gris). Une case ajustée est surlignée et conservée pour les feuillets, sommaires et fichiers XML.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Fermer" data-testid="paie-yearend-boxes-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5 max-h-[65vh] overflow-y-auto">
          <Grid title="T4 — cases principales" items={T4} values={t4} computed={row.computed?.t4} onChange={(k, v) => setT4((s) => ({ ...s, [k]: v }))} prefix="paie-yearend-box-t4" />
          <Grid title="T4 — autres renseignements (codes)" items={T4_OTHER} values={other} computed={row.computed?.t4?.other} onChange={(k, v) => setOther((s) => ({ ...s, [k]: v }))} prefix="paie-yearend-box-t4other" />
          <Grid title="Relevé 1 — cases" items={RL1} values={rl1} computed={row.computed?.rl1} onChange={(k, v) => setRl1((s) => ({ ...s, [k]: v }))} prefix="paie-yearend-box-rl1" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 p-5 border-t">
          <Btn tone="slate" icon={RotateCcw} busy={busy} onClick={() => save(true)} testId="paie-yearend-boxes-reset">Réinitialiser (valeurs calculées)</Btn>
          <div className="flex gap-2">
            <Btn tone="ghost" onClick={onClose} testId="paie-yearend-boxes-cancel">Annuler</Btn>
            <Btn tone="sky" icon={Save} busy={busy} onClick={() => save(false)} testId="paie-yearend-boxes-save">Enregistrer les ajustements</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
